import { heroData } from "../data/mock";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { motion } from "framer-motion";
import { HeroVisual } from "./HeroVisual";

export const HeroSection = () => {
  const scrollToServices = () => {
    const el = document.querySelector("#services");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToContact = () => {
    const el = document.querySelector("#contact");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      className="relative w-full min-h-screen flex items-center overflow-hidden"
      style={{ background: "#0F1225", paddingTop: "80px" }}
    >
      {/* Subtle grid overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(123,104,238,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(123,104,238,0.04) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      {/* Glow effects */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: "15%",
          right: "15%",
          width: "500px",
          height: "500px",
          background: "radial-gradient(circle, rgba(123,104,238,0.08) 0%, transparent 70%)",
          filter: "blur(80px)",
        }}
      />
      <div
        className="absolute pointer-events-none"
        style={{
          bottom: "10%",
          left: "5%",
          width: "400px",
          height: "400px",
          background: "radial-gradient(circle, rgba(96,165,250,0.06) 0%, transparent 70%)",
          filter: "blur(60px)",
        }}
      />

      <div className="w-full flex flex-col lg:flex-row items-center" style={{ padding: "0 7.6923%" }}>
        {/* Left Content */}
        <div className="flex-1 z-10 py-16 lg:py-0">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            {/* Overline */}
            <div className="flex items-center gap-3 mb-8">
              <div className="w-8 h-[2px] rounded-full" style={{ background: "#7B68EE" }} />
              <span
                className="text-sm tracking-widest uppercase"
                style={{ color: "#7B68EE", fontFamily: "'Kode Mono', monospace" }}
              >
                Premium IT Solutions
              </span>
            </div>

            {/* Headline */}
            <h1
              className="leading-tight mb-6"
              style={{
                fontFamily: "'Kode Mono', monospace",
                fontWeight: 600,
                fontSize: "clamp(40px, 5.5vw, 66px)",
                letterSpacing: "-0.62px",
                color: "#F0F0FF",
                lineHeight: 1.1,
              }}
            >
              {heroData.headline}
              <br />
              <span style={{ color: "#8585A8" }}>
                {heroData.subheadline}
              </span>
            </h1>

            {/* Description */}
            <p
              className="max-w-xl mb-12"
              style={{
                fontFamily: "'Kode Mono', monospace",
                fontSize: "18px",
                lineHeight: 1.6,
                color: "#9494B8",
                fontWeight: 400,
              }}
            >
              {heroData.description}
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap gap-4">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToContact}
                className="inline-flex items-center gap-3 border-none cursor-pointer"
                style={{
                  background: "#7B68EE",
                  color: "#FFFFFF",
                  padding: "16px 32px",
                  fontSize: "18px",
                  fontWeight: 500,
                  borderRadius: "8px",
                  fontFamily: "'Kode Mono', monospace",
                  minHeight: "56px",
                  transition: "background 0.4s ease-in-out",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#6A58DD"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "#7B68EE"; }}
              >
                {heroData.ctaPrimary}
                <ArrowUpRight size={20} />
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToServices}
                className="inline-flex items-center gap-3 cursor-pointer"
                style={{
                  background: "rgba(123,104,238,0.1)",
                  color: "#C4B5FD",
                  padding: "16px 32px",
                  fontSize: "18px",
                  fontWeight: 500,
                  borderRadius: "8px",
                  fontFamily: "'Kode Mono', monospace",
                  minHeight: "56px",
                  border: "1px solid rgba(123,104,238,0.25)",
                  transition: "background 0.4s ease-in-out, border-color 0.4s ease-in-out",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(123,104,238,0.18)";
                  e.currentTarget.style.borderColor = "rgba(123,104,238,0.4)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(123,104,238,0.1)";
                  e.currentTarget.style.borderColor = "rgba(123,104,238,0.25)";
                }}
              >
                {heroData.ctaSecondary}
              </motion.button>
            </div>
          </motion.div>
        </div>

        {/* Right - Stunning Visual */}
        <div className="flex-1 relative hidden lg:block" style={{ height: "700px", overflow: "visible" }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.3, ease: "easeOut" }}
            className="w-full h-full"
          >
            <HeroVisual />
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 cursor-pointer z-10"
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
        onClick={scrollToServices}
      >
        <span
          className="text-xs uppercase tracking-widest"
          style={{ color: "#6B6B8D", fontFamily: "'Kode Mono', monospace" }}
        >
          Scroll
        </span>
        <ArrowDown size={16} style={{ color: "#6B6B8D" }} />
      </motion.div>
    </section>
  );
};
